import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  X, RefreshCw, CheckCircle, AlertCircle, Clock,
  Brain, FileText, Eye, Archive, Zap, Filter, ShieldAlert,
} from 'lucide-react';
import { Stats, SEVERITY_ORDER, SourceStats, Decision, CoverageDocument, FilteredEvent } from '@/types';
import { api } from '@/services/api';
import { formatTime } from '@/utils/formatting';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { derivePipelineNumbers } from '@/lib/pipeline';

interface StageDetailPanelProps {
  activeStage: string | null;
  onClose: () => void;
  stats: Stats | null;
  actionThreshold: number;
  relevanceThreshold: number;
}

export function StageDetailPanel({
  activeStage,
  onClose,
  stats,
  actionThreshold,
  relevanceThreshold,
}: StageDetailPanelProps) {
  const titles: Record<string, string> = {
    watch: 'Event Collection',
    prescreen: 'Pre-Filter',
    decide: 'AI Analysis',
    relevant: 'Relevant Events',
    cover: 'Covered Events & Coverage',
    monitor: 'Monitoring',
    ignored: 'Irrelevant Events',
  };

  const icons: Record<string, React.ReactNode> = {
    watch: <Eye className="w-5 h-5 text-primary" />,
    prescreen: <Zap className="w-5 h-5 text-orange-500" />,
    decide: <Brain className="w-5 h-5 text-sabio-yellow" />,
    relevant: <AlertCircle className="w-5 h-5 text-blue-500" />,
    cover: <FileText className="w-5 h-5 text-green-500" />,
    monitor: <Eye className="w-5 h-5 text-blue-500" />,
    ignored: <Archive className="w-5 h-5 text-muted-foreground" />,
  };

  return (
    <AnimatePresence>
      {activeStage && (
        <>
          {/* Overlay */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-40 bg-black/50 backdrop-blur-sm"
            onClick={onClose}
          />

          {/* Panel */}
          <motion.div
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'spring', damping: 30, stiffness: 300 }}
            className="fixed top-0 right-0 z-50 h-full w-full max-w-2xl border-l border-border bg-background shadow-2xl flex flex-col"
          >
            {/* Header — fixed, never scrolls */}
            <div className="flex items-center justify-between border-b border-border px-6 py-4 shrink-0">
              <div className="flex items-center gap-3">
                {icons[activeStage]}
                <h2 className="text-xl font-bold text-foreground">
                  {titles[activeStage] || 'Details'}
                </h2>
              </div>
              <Button variant="ghost" size="icon-sm" onClick={onClose}>
                <X className="w-5 h-5" />
              </Button>
            </div>

            {/* Scrollable content area — single scroll container, no nested ScrollAreas */}
            <div className="flex-1 overflow-y-auto overscroll-contain p-6">
              <StageContent
                stage={activeStage}
                stats={stats}
                actionThreshold={actionThreshold}
                relevanceThreshold={relevanceThreshold}
              />
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}

// ── Stage Router ──────────────────────────────────────────────

interface StageContentProps {
  stage: string;
  stats: Stats | null;
  actionThreshold: number;
  relevanceThreshold: number;
}

function StageContent({ stage, stats, actionThreshold, relevanceThreshold }: StageContentProps) {
  if (!stats) {
    return (
      <div className="flex items-center justify-center h-32">
        <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  switch (stage) {
    case 'watch':
      return <WatchStageContent stats={stats} />;
    case 'prescreen':
      return <PreScreenStageContent stats={stats} />;
    case 'decide':
      return <DecideStageContent stats={stats} actionThreshold={actionThreshold} relevanceThreshold={relevanceThreshold} />;
    case 'relevant':
      return <RelevantStageContent stats={stats} actionThreshold={actionThreshold} relevanceThreshold={relevanceThreshold} />;
    case 'cover':
      return <CoverStageContent stats={stats} actionThreshold={actionThreshold} />;
    case 'monitor':
      return <MonitorStageContent stats={stats} actionThreshold={actionThreshold} relevanceThreshold={relevanceThreshold} />;
    case 'ignored':
      return <IgnoredStageContent stats={stats} relevanceThreshold={relevanceThreshold} />;
    default:
      return null;
  }
}

// ── Pre-Screen ─────────────────────────────────────────────────

function PreScreenStageContent({ stats }: { stats: Stats }) {
  const [filteredEvents, setFilteredEvents] = useState<FilteredEvent[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.getFilteredEvents(20)
      .then(e => { setFilteredEvents(e || []); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  const filtered = stats.pre_screen_filtered || 0;
  const ruleFiltered = stats.rule_filtered || 0;
  const passed = stats.pre_screen_passed || 0;
  const skipped = stats.pre_screen_skipped || 0;
  const through = passed + skipped;
  const evaluated = filtered + ruleFiltered + through;
  const filterRate = evaluated > 0 ? Math.round(((filtered + ruleFiltered) / evaluated) * 100) : 0;

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <StatBox label="Through" value={through} accent="text-green-500" />
        <StatBox label="LLM Filtered" value={filtered} accent="text-orange-500" />
        <StatBox label="Rule Filtered" value={ruleFiltered} accent="text-orange-500" />
        <StatBox label="Filter Rate" value={`${filterRate}%`} accent="text-orange-600 dark:text-orange-400" />
      </div>

      <Card>
        <CardContent className="p-4">
          <p className="text-muted-foreground text-sm mb-2 font-medium">How it works</p>
          <p className="text-sm text-foreground/80">
            Events pass through a lightweight pre-filter (rules + optional cheap LLM) that quickly checks relevance before the full scoring pipeline.
            Obviously irrelevant events are filtered out, saving cost on the expensive full analysis.
            High-confidence filters can be overridden by reviewers from the Pre-Screened tab.
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-4">
          <p className="text-muted-foreground text-sm mb-3 flex items-center gap-2 font-medium">
            <Filter className="w-4 h-4" />
            Recently Filtered Events
          </p>
          {loading ? <LoadingSpinner /> : filteredEvents.length === 0 ? <EmptyState text="No filtered events yet" /> : (
            <div className="space-y-2 max-h-[40vh] overflow-y-auto overscroll-contain pr-1">
              {filteredEvents.map(e => (
                <div key={e.id} className="p-3 rounded-lg bg-orange-500/5 border border-orange-500/20 hover:border-orange-500/40 transition-colors">
                  <div className="flex items-start gap-3">
                    <ShieldAlert className="w-5 h-5 text-orange-500 mt-0.5 shrink-0" />
                    <div className="flex-1 min-w-0 overflow-hidden">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <Badge className="h-4 text-[10px] px-1.5 shrink-0 bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300">
                          {e.pre_screen_verdict || 'filtered'}
                        </Badge>
                        {e.pre_screen_model && (
                          <span className="text-[10px] text-muted-foreground">{e.pre_screen_model}</span>
                        )}
                        <span className="text-[10px] text-muted-foreground truncate">{e.source_id}</span>
                        {e.decided_at && (
                          <span className="text-[10px] text-muted-foreground/60 ml-auto shrink-0">{formatTime(e.decided_at)}</span>
                        )}
                      </div>
                      <p className="text-sm text-foreground truncate" title={e.title}>{e.title}</p>
                      {(e.pre_screen_reasoning || e.reasoning) && (
                        <p className="text-xs text-muted-foreground truncate mt-0.5" title={e.pre_screen_reasoning || e.reasoning || ''}>
                          {e.pre_screen_reasoning || e.reasoning}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// ── Decide ────────────────────────────────────────────────────

function DecideStageContent({ stats, actionThreshold, relevanceThreshold }: { stats: Stats; actionThreshold: number; relevanceThreshold: number }) {
  const [recentDecisions, setRecentDecisions] = useState<Decision[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.getDecisions(undefined, 20)
      .then(d => { setRecentDecisions(d || []); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  const thresholdPct = Math.round(actionThreshold * 100);
  const relevancePct = Math.round(relevanceThreshold * 100);
  const p = derivePipelineNumbers(stats);

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        <StatBox label="LLM Evaluated" value={p.llmDecided} />
        <StatBox label="Avg Score" value={p.llmDecided > 0 ? `${p.avgLLMScorePercent}%` : '—'} accent="text-sabio-yellow" />
        <StatBox label="Threshold" value={`${thresholdPct}%`} accent="text-green-600 dark:text-green-400" />
      </div>

      {/* Severity Distribution */}
      <Card>
        <CardContent className="p-4">
          <p className="text-muted-foreground text-sm mb-3 font-medium">Severity Distribution</p>
          <div className="space-y-2.5">
            {SEVERITY_ORDER
              .filter(sev => stats.by_severity?.[sev])
              .map(sev => {
                const count = stats.by_severity[sev];
                const total = p.relevant || 1;
                const pct = Math.round((count / total) * 100);
                return (
                  <div key={sev} className="space-y-1">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Badge variant={sev as any} className="h-5 text-[10px]">{sev}</Badge>
                      </div>
                      <span className="text-muted-foreground font-semibold text-sm">{count} ({pct}%)</span>
                    </div>
                    <div className="w-full bg-muted rounded-full h-1.5">
                      <div
                        className={cn(
                          'h-1.5 rounded-full transition-all',
                          sev === 'critical' && 'bg-red-500',
                          sev === 'high' && 'bg-sabio-yellow',
                          sev === 'medium' && 'bg-sabio-purple',
                          sev === 'low' && 'bg-green-500',
                        )}
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                  </div>
                );
              })}
          </div>
        </CardContent>
      </Card>

      {/* Score Ranges */}
      <Card>
        <CardContent className="p-4">
          <p className="text-muted-foreground text-sm mb-3 font-medium">Score Ranges</p>
          <div className="grid grid-cols-3 gap-3">
            <div className="text-center p-3 rounded-lg bg-green-500/10 border border-green-500/20">
              <div className="text-xl font-bold text-green-600 dark:text-green-400">{stats.above_threshold_count || 0}</div>
              <div className="text-[11px] text-muted-foreground">&ge;{thresholdPct}% Actionable</div>
            </div>
            <div className="text-center p-3 rounded-lg bg-blue-500/10 border border-blue-500/20">
              <div className="text-xl font-bold text-blue-600 dark:text-blue-400">{Math.max(0, p.relevant - (stats.above_threshold_count || 0))}</div>
              <div className="text-[11px] text-muted-foreground">{relevancePct}–{thresholdPct}% Monitor</div>
            </div>
            <div className="text-center p-3 rounded-lg bg-muted/50 border border-border">
              <div className="text-xl font-bold text-muted-foreground">{p.irrelevant}</div>
              <div className="text-[11px] text-muted-foreground">&lt;{relevancePct}% Irrelevant</div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Recent Decisions */}
      <Card>
        <CardContent className="p-4">
          <p className="text-muted-foreground text-sm mb-3 flex items-center gap-2 font-medium">
            <Brain className="w-4 h-4" />
            Recent Decisions
          </p>
          {loading ? <LoadingSpinner /> : recentDecisions.length === 0 ? <EmptyState text="No decisions yet" /> : (
            <div className="space-y-2 max-h-[40vh] overflow-y-auto overscroll-contain pr-1">
              {recentDecisions.slice(0, 10).map(d => (
                <MiniDecisionCard key={d.id} decision={d} />
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// ── Cover ─────────────────────────────────────────────────────

function CoverStageContent({ stats, actionThreshold }: { stats: Stats; actionThreshold: number }) {
  const [actionableDecisions, setActionableDecisions] = useState<Decision[]>([]);
  const [coverageDocs, setCoverageDocs] = useState<CoverageDocument[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([api.getDecisions(true, 50), api.getCoverageDocuments()])
      .then(([decisions, docs]) => {
        setActionableDecisions((decisions || []).filter(d => d.relevance_score >= actionThreshold));
        setCoverageDocs(docs || []);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [actionThreshold]);

  const thresholdPct = Math.round(actionThreshold * 100);

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        <StatBox label="Actionable" value={stats.above_threshold_count || 0} accent="text-orange-600 dark:text-orange-400" />
        <StatBox label="Coverage Docs" value={stats.coverage_count || 0} accent="text-green-600 dark:text-green-400" />
        <StatBox label="Threshold" value={`≥${thresholdPct}%`} accent="text-green-600 dark:text-green-400" />
      </div>

      <Card className="border-green-500/20">
        <CardContent className="p-4">
          <p className="text-muted-foreground text-sm mb-3 flex items-center gap-2 font-medium">
            <FileText className="w-4 h-4 text-green-600 dark:text-green-400" />
            Generated Coverage Documents
          </p>
          {loading ? <LoadingSpinner /> : coverageDocs.length === 0 ? <EmptyState text="No coverage documents generated yet" /> : (
            <div className="space-y-2 max-h-[30vh] overflow-y-auto overscroll-contain pr-1">
              {coverageDocs.map(doc => <MiniCoverageCard key={doc.id} doc={doc} />)}
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-4">
          <p className="text-muted-foreground text-sm mb-3 flex items-center gap-2 font-medium">
            <Zap className="w-4 h-4 text-orange-600 dark:text-orange-400" />
            Actionable Events (score &ge;{thresholdPct}%)
          </p>
          {loading ? <LoadingSpinner /> : actionableDecisions.length === 0 ? <EmptyState text="No actionable events" /> : (
            <div className="space-y-2 max-h-[40vh] overflow-y-auto overscroll-contain pr-1">
              {actionableDecisions.slice(0, 15).map(d => <MiniDecisionCard key={d.id} decision={d} />)}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// ── Relevant ──────────────────────────────────────────────────

function RelevantStageContent({ stats, actionThreshold, relevanceThreshold }: { stats: Stats; actionThreshold: number; relevanceThreshold: number }) {
  const [relevantDecisions, setRelevantDecisions] = useState<Decision[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.getDecisions(true, 50)
      .then(decisions => { setRelevantDecisions(decisions || []); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  const p = derivePipelineNumbers(stats);
  const relevancePct = Math.round(relevanceThreshold * 100);
  const thresholdPct = Math.round(actionThreshold * 100);

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <StatBox label="Relevant" value={p.relevant} accent="text-blue-600 dark:text-blue-400" />
        <StatBox label="Actionable" value={stats.above_threshold_count || 0} accent="text-green-600 dark:text-green-400" />
        <StatBox label="Monitoring" value={p.relevantUncovered} accent="text-blue-500" />
        <StatBox label="Covered" value={p.covered} accent="text-green-600 dark:text-green-400" />
      </div>

      {/* Severity Distribution */}
      <Card>
        <CardContent className="p-4">
          <p className="text-muted-foreground text-sm mb-3 font-medium">Severity Distribution</p>
          <div className="space-y-2.5">
            {SEVERITY_ORDER
              .filter(sev => stats.by_severity?.[sev])
              .map(sev => {
                const count = stats.by_severity[sev];
                const total = p.relevant || 1;
                const pct = Math.round((count / total) * 100);
                return (
                  <div key={sev} className="space-y-1">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Badge variant={sev as any} className="h-5 text-[10px]">{sev}</Badge>
                      </div>
                      <span className="text-muted-foreground font-semibold text-sm">{count} ({pct}%)</span>
                    </div>
                    <div className="w-full bg-muted rounded-full h-1.5">
                      <div
                        className={cn(
                          'h-1.5 rounded-full transition-all',
                          sev === 'critical' && 'bg-red-500',
                          sev === 'high' && 'bg-sabio-yellow',
                          sev === 'medium' && 'bg-sabio-purple',
                          sev === 'low' && 'bg-green-500',
                        )}
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                  </div>
                );
              })}
          </div>
        </CardContent>
      </Card>

      <Card className="border-blue-500/20">
        <CardContent className="p-4">
          <p className="text-muted-foreground text-sm mb-2 font-medium">About Relevant Events</p>
          <p className="text-muted-foreground text-sm leading-relaxed">
            These events scored above the relevance threshold ({relevancePct}%). Events scoring
            above the action threshold ({thresholdPct}%) are actionable and will get coverage
            documents. The remainder are tracked for monitoring.
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-4">
          <p className="text-muted-foreground text-sm mb-3 flex items-center gap-2 font-medium">
            <AlertCircle className="w-4 h-4 text-blue-600 dark:text-blue-400" />
            Recent Relevant Events
          </p>
          {loading ? <LoadingSpinner /> : relevantDecisions.length === 0 ? <EmptyState text="No relevant events yet" /> : (
            <div className="space-y-2 max-h-[40vh] overflow-y-auto overscroll-contain pr-1">
              {relevantDecisions.slice(0, 15).map(d => <MiniDecisionCard key={d.id} decision={d} />)}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// ── Monitor ───────────────────────────────────────────────────

function MonitorStageContent({ stats, actionThreshold, relevanceThreshold }: { stats: Stats; actionThreshold: number; relevanceThreshold: number }) {
  const [monitorDecisions, setMonitorDecisions] = useState<Decision[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.getDecisions(true, 100)
      .then(decisions => {
        setMonitorDecisions((decisions || []).filter(d =>
          d.relevance_score >= relevanceThreshold && d.relevance_score < actionThreshold
        ));
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [actionThreshold, relevanceThreshold]);

  const p = derivePipelineNumbers(stats);
  const relevancePct = Math.round(relevanceThreshold * 100);
  const thresholdPct = Math.round(actionThreshold * 100);

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3">
        <StatBox label="Monitored" value={p.relevantUncovered} accent="text-blue-600 dark:text-blue-400" />
        <StatBox label="Score Range" value={`${relevancePct}–${thresholdPct}%`} accent="text-blue-600 dark:text-blue-400" />
      </div>

      <Card className="border-blue-500/20">
        <CardContent className="p-4">
          <p className="text-muted-foreground text-sm mb-2 font-medium">What "Monitor" means</p>
          <p className="text-muted-foreground text-sm leading-relaxed">
            These events scored above the relevance threshold ({relevancePct}%) but below the action
            threshold ({thresholdPct}%). They are tracked for potential escalation if the situation
            develops, but don't currently warrant coverage documents or alerts.
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-4">
          <p className="text-muted-foreground text-sm mb-3 flex items-center gap-2 font-medium">
            <Eye className="w-4 h-4 text-blue-600 dark:text-blue-400" />
            Monitored Events
          </p>
          {loading ? <LoadingSpinner /> : monitorDecisions.length === 0 ? <EmptyState text="No events currently being monitored" /> : (
            <div className="space-y-2 max-h-[50vh] overflow-y-auto overscroll-contain pr-1">
              {monitorDecisions.map(d => <MiniDecisionCard key={d.id} decision={d} />)}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// ── Ignored ───────────────────────────────────────────────────

function IgnoredStageContent({ stats, relevanceThreshold }: { stats: Stats; relevanceThreshold: number }) {
  const [ignoredDecisions, setIgnoredDecisions] = useState<Decision[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.getDecisions(undefined, 100)
      .then(decisions => {
        setIgnoredDecisions((decisions || []).filter(d => !d.is_relevant));
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  const ignoredCount = derivePipelineNumbers(stats).irrelevant;
  const relevancePct = Math.round(relevanceThreshold * 100);

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3">
        <StatBox label="Irrelevant" value={ignoredCount} />
        <StatBox label="Threshold" value={`< ${relevancePct}%`} />
      </div>

      <Card>
        <CardContent className="p-4">
          <p className="text-muted-foreground text-sm mb-2 font-medium">Why events are irrelevant</p>
          <ul className="space-y-1.5 text-sm text-muted-foreground list-disc list-inside">
            <li>Not related to tenant's business operations or customer impact</li>
            <li>General news without specific operational relevance</li>
            <li>LLM scored below the {relevancePct}% relevance threshold</li>
          </ul>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-4">
          <p className="text-muted-foreground text-sm mb-3 flex items-center gap-2 font-medium">
            <Archive className="w-4 h-4" />
            Recent Irrelevant Events
          </p>
          {loading ? <LoadingSpinner /> : ignoredDecisions.length === 0 ? <EmptyState text="No irrelevant events" /> : (
            <div className="space-y-2 max-h-[50vh] overflow-y-auto overscroll-contain pr-1">
              {ignoredDecisions.slice(0, 20).map(d => <MiniDecisionCard key={d.id} decision={d} />)}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// ── Watch ─────────────────────────────────────────────────────

function WatchStageContent({ stats }: { stats: Stats }) {
  const [sourceStats, setSourceStats] = useState<SourceStats[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const fetchSourceStats = async () => {
    try {
      const data = await api.getSourceStats();
      setSourceStats(data || []);
    } catch (err) {
      console.error('Failed to fetch source stats:', err);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchSourceStats();
    const interval = setInterval(fetchSourceStats, 30000);
    return () => clearInterval(interval);
  }, []);

  const handleRefresh = () => {
    setRefreshing(true);
    fetchSourceStats();
  };

  const formatRelativeTime = (dateStr: string) => {
    if (!dateStr) return 'Never';
    const date = new Date(dateStr);
    const diffMs = Date.now() - date.getTime();
    const diffSecs = Math.floor(diffMs / 1000);
    const diffMins = Math.floor(diffSecs / 60);
    const diffHours = Math.floor(diffMins / 60);
    if (diffSecs < 60) return `${diffSecs}s ago`;
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    return date.toLocaleDateString();
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3">
        <StatBox label="Total Events" value={stats.total_events || 0} />
        <StatBox label="Active Sources" value={Object.keys(stats.by_source || {}).length} accent="text-blue-600 dark:text-blue-400" />
      </div>

      {/* Source Polling */}
      <Card>
        <CardContent className="p-4">
          <div className="flex items-center justify-between mb-3">
            <p className="text-muted-foreground text-sm font-medium">Source Polling Status</p>
            <Button variant="ghost" size="icon-sm" onClick={handleRefresh} disabled={refreshing}>
              <RefreshCw className={cn('w-4 h-4', refreshing && 'animate-spin')} />
            </Button>
          </div>

          {loading ? <LoadingSpinner /> : sourceStats.length === 0 ? (
            <EmptyState text="No polling data yet. Waiting for first collection cycle..." />
          ) : (
            <div className="space-y-2 max-h-[40vh] overflow-y-auto overscroll-contain pr-1">
              {sourceStats
                .sort((a, b) => new Date(b.last_polled).getTime() - new Date(a.last_polled).getTime())
                .map((source) => (
                  <div key={source.source_id} className="p-3 rounded-lg bg-muted/30 border border-border/50">
                    <div className="flex items-center justify-between mb-1">
                      <div className="flex items-center gap-2 min-w-0">
                        {source.last_poll_status === 'success' ? (
                          <CheckCircle className="w-4 h-4 text-green-600 dark:text-green-400 shrink-0" />
                        ) : (
                          <AlertCircle className="w-4 h-4 text-red-500 shrink-0" />
                        )}
                        <span className="text-foreground text-sm font-medium truncate">{source.source_id}</span>
                      </div>
                      <div className="flex items-center gap-1 text-muted-foreground text-xs shrink-0 ml-2">
                        <Clock className="w-3 h-3" />
                        {formatRelativeTime(source.last_polled)}
                      </div>
                    </div>

                    <div className="flex items-center gap-4 text-xs mt-2 flex-wrap">
                      <span><span className="text-muted-foreground">Found:</span>{' '}<span className="text-blue-600 dark:text-blue-400 font-medium">{source.items_found}</span></span>
                      <span><span className="text-muted-foreground">New:</span>{' '}<span className="text-green-600 dark:text-green-400 font-medium">{source.new_items}</span></span>
                      <span><span className="text-muted-foreground">Dupes:</span>{' '}<span className="text-muted-foreground font-medium">{source.duplicate_items}</span></span>
                      <span className="ml-auto"><span className="text-muted-foreground">Polls:</span>{' '}<span className="text-muted-foreground">{source.total_polls}</span></span>
                    </div>

                    {source.last_error && (
                      <div className="mt-2 text-xs text-red-600 dark:text-red-400 truncate" title={source.last_error}>
                        Error: {source.last_error}
                      </div>
                    )}
                  </div>
                ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Events by Source */}
      <Card>
        <CardContent className="p-4">
          <p className="text-muted-foreground text-sm mb-3 font-medium">Events by Source</p>
          <div className="space-y-2 max-h-[30vh] overflow-y-auto overscroll-contain pr-1">
            {Object.entries(stats.by_source || {})
              .sort(([, a], [, b]) => b - a)
              .map(([src, count]) => (
                <div key={src} className="flex items-center justify-between">
                  <span className="text-foreground text-sm truncate mr-2">{src}</span>
                  <span className="text-primary font-semibold shrink-0">{count}</span>
                </div>
              ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// ── Shared Components ─────────────────────────────────────────

function StatBox({ label, value, accent }: { label: string; value: number | string; accent?: string }) {
  return (
    <Card>
      <CardContent className="p-3 sm:p-4">
        <div className="text-muted-foreground text-xs sm:text-sm">{label}</div>
        <div className={cn('text-xl sm:text-2xl font-bold text-foreground truncate', accent)}>{value}</div>
      </CardContent>
    </Card>
  );
}

function MiniDecisionCard({ decision }: { decision: Decision }) {
  const score = Math.round(decision.relevance_score * 100);
  const scoreColor = score >= 75 ? 'text-green-600 dark:text-green-400' : score >= 30 ? 'text-sabio-yellow' : 'text-muted-foreground';

  return (
    <div className="p-3 rounded-lg bg-muted/30 border border-border/50 hover:border-primary/30 transition-colors">
      <div className="flex items-start gap-3">
        <div className={cn('text-lg font-bold w-10 text-center shrink-0 tabular-nums', scoreColor)}>
          {score}
        </div>
        <div className="flex-1 min-w-0 overflow-hidden">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <Badge variant={decision.severity as any} className="h-4 text-[10px] px-1.5 shrink-0">
              {decision.severity}
            </Badge>
            <span className="text-[10px] text-muted-foreground truncate">{decision.event_source}</span>
            <span className="text-[10px] text-muted-foreground/60 ml-auto shrink-0">{formatTime(decision.decided_at)}</span>
          </div>
          <p className="text-sm text-foreground truncate" title={decision.event_title}>{decision.event_title}</p>
          <p className="text-xs text-muted-foreground truncate mt-0.5" title={decision.reasoning}>{decision.reasoning}</p>
        </div>
      </div>
    </div>
  );
}

function MiniCoverageCard({ doc }: { doc: CoverageDocument }) {
  const meta = doc.metadata as { event_title?: string; severity?: string; score?: number } | undefined;
  const title = meta?.event_title || 'Coverage Document';
  const score = meta?.score ? Math.round(meta.score * 100) : null;

  return (
    <div className="p-3 rounded-lg bg-muted/30 border border-border/50 hover:border-green-500/30 transition-colors">
      <div className="flex items-start gap-3">
        <FileText className="w-5 h-5 text-green-600 dark:text-green-400 mt-0.5 shrink-0" />
        <div className="flex-1 min-w-0 overflow-hidden">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <Badge variant={doc.workflow_status as any} className="h-4 text-[10px] px-1.5 shrink-0">
              {doc.workflow_status}
            </Badge>
            {score && <span className="text-[10px] text-muted-foreground">Score: {score}%</span>}
            <span className="text-[10px] text-muted-foreground/60 ml-auto shrink-0">v{doc.version}</span>
          </div>
          <p className="text-sm text-foreground truncate" title={title}>{title}</p>
          <p className="text-xs text-muted-foreground mt-0.5">{formatTime(doc.created_at)}</p>
        </div>
      </div>
    </div>
  );
}

function LoadingSpinner() {
  return (
    <div className="flex items-center justify-center py-4">
      <div className="animate-spin w-6 h-6 border-2 border-primary border-t-transparent rounded-full" />
    </div>
  );
}

function EmptyState({ text }: { text: string }) {
  return (
    <div className="text-muted-foreground text-sm py-4 text-center">{text}</div>
  );
}
